import { ChangeDetectionStrategy, Component, HostBinding, Input, OnInit } from '@angular/core';
import { FeatureService, HintComponent, ShopperService, ShopperState, SVGIcon } from '@woolworthsnz/styleguide';
import { ContextResponse, PriceResponse, ProductResponse } from '@woolworthsnz/trader-api';
import { ProductService } from '../../services';
import { LayoutType } from '../../models/layout-type.model';
import { NgIf, NgClass, CurrencyPipe, AsyncPipe } from '@angular/common';
import { ProductSource } from '../../models/tealium-events';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export type PriceType = 'Was' | 'Save' | 'Non-member';

@Component({
	selector: 'product-price',
	templateUrl: './price.component.html',
	styleUrls: ['./price.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, NgClass, HintComponent, CurrencyPipe, AsyncPipe],
})
export class PriceComponent implements OnInit {
	@Input() price: PriceResponse;
	@Input() uniqueId: number;
	@Input() unit: ProductResponse.UnitEnum;
	@Input() supportsEachAndKgPricing: boolean;
	@Input() selectedPurchasingUnit: ProductResponse.SelectedPurchasingUnitEnum;
	@Input() shouldZoomForBigPrice = true;
	@Input() isProductTagClubPrice = false;

	@HostBinding('attr.data-layout')
	@Input()
	layout: LayoutType = LayoutType.grid;
	iconHint = SVGIcon.Hint;
	shopper: ShopperState;
	isChangingOrder: boolean;
	haystackEDRChangesEnabled$ = this.featureService
		.isEnabled(ContextResponse.EnabledFeaturesEnum.HaystackEDRChanges)
		.pipe(takeUntilDestroyed());
	haystackEDRChangesEnabled: boolean;
	shopperState$ = this.shopperService.state$.pipe(takeUntilDestroyed());

	@HostBinding('attr.data-zoom') get zoomForBigPrice(): boolean {
		return this.shouldZoomForBigPrice ? (this.price?.salePrice || 0) > 99.99 : false;
	}

	constructor(
		private shopperService: ShopperService,
		private productService: ProductService,
		private featureService: FeatureService
	) {}

	ngOnInit(): void {
		this.shopperState$.subscribe((shopper: ShopperState) => {
			this.shopper = shopper;
			this.isChangingOrder = shopper.isChangingOrder ?? false;
		});

		this.haystackEDRChangesEnabled$.subscribe((haystackEDRChangesEnabled) => {
			this.haystackEDRChangesEnabled = haystackEDRChangesEnabled;
		});
	}

	get isKgItem(): boolean {
		return this.unit === ProductResponse.UnitEnum.Kg;
	}

	get isEachItem(): boolean {
		return this.unit === ProductResponse.UnitEnum.Each;
	}

	get hasLayoutList(): boolean {
		return this.layout === LayoutType.list;
	}

	get isClubPrice(): boolean {
		if (
			!this.price ||
			!this.price.canShowOriginalPrice ||
			(this.enabledIndicateChangedPrice && !this.showOriginalPriceAsWasPriceForItemWithChangedPrice)
		) {
			return false;
		}

		return this.price?.isClubPrice === true;
	}

	get showNonMember(): boolean {
		return (
			this.haystackEDRChangesEnabled &&
			this.isProductTagClubPrice &&
			(this.isClubPrice || (!this.isClubPrice && this.showSavings))
		);
	}

	get showWasPrice(): boolean {
		return (
			((!this.haystackEDRChangesEnabled && !this.isClubPrice && this.showSavings) ||
				(this.haystackEDRChangesEnabled && !this.isProductTagClubPrice && this.showSavings)) &&
			this.canShowSavings
		);
	}

	get showSavePrice(): boolean {
		return (
			this.showSavings &&
			!this.hasLayoutList &&
			(!this.haystackEDRChangesEnabled || (this.haystackEDRChangesEnabled && !this.showNonMember)) &&
			this.canShowSavings
		);
	}

	get hasDiscountPrice(): boolean {
		return (
			(!!this.price?.isSpecial || this.isClubPrice || !!this.price?.isTargetedOffer) && this.price.savePrice !== 0
		);
	}

	get showSavings(): boolean {
		if (
			!this.price ||
			!this.price.canShowOriginalPrice ||
			(this.enabledIndicateChangedPrice &&
				!this.showOriginalPriceAsWasPriceForItemWithChangedPrice &&
				this.hasLayoutList)
		) {
			return false;
		}

		return !!this.hasDiscountPrice;
	}

	get enabledIndicateChangedPrice(): boolean {
		return this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.ChangeOrderIndicateChangedPriceInProductList
		);
	}

	get showPresentPrice(): boolean {
		return (
			!this.enabledIndicateChangedPrice ||
			!this.isChangingOrder ||
			this.price?.orderedPrice === null ||
			this.price?.orderedPrice === this.price?.salePrice
		);
	}

	get showOrderedPriceAsWasPriceForItemWithChangedPrice(): boolean {
		return (
			!this.showPresentPrice &&
			this.isChangingOrder &&
			!this.usesOrderedPriceAsCurrent &&
			this.price?.orderedPrice !== null &&
			this.price?.orderedPrice !== undefined
		);
	}

	get showOriginalPriceAsWasPriceForItemWithChangedPrice(): boolean {
		return (
			!this.showOrderedPriceAsWasPriceForItemWithChangedPrice &&
			this.usesOrderedPriceAsCurrent &&
			this.price &&
			this.price?.originalPrice !== null &&
			this.price?.originalPrice !== undefined &&
			this.price?.orderedPrice !== null &&
			this.price?.orderedPrice !== undefined &&
			this.price?.orderedPrice !== this.price?.originalPrice
		);
	}

	get usesOrderedPriceAsCurrent(): boolean {
		return (
			this.price &&
			this.price.orderedPrice !== null &&
			this.price.orderedPrice !== undefined &&
			this.price.orderedPrice !== this.price.salePrice &&
			this.price.isUsingOrderedPrice !== undefined &&
			this.price.isUsingOrderedPrice
		);
	}

	get isSpecial(): boolean {
		if (!this.price) {
			return false;
		}

		const { canShowOriginalPrice, isClubPrice } = this.price;

		if (isClubPrice && !canShowOriginalPrice && (!this.shopper.isShopper || !this.shopper.hasOnecard)) {
			return false;
		}

		if (isClubPrice && this.haystackEDRChangesEnabled) {
			return false;
		}

		return this.hasDiscountPrice;
	}

	get presentPrice():
		| { dollars: number; price: number; cents?: undefined }
		| { dollars: string; cents: string; price?: undefined } {
		if (!this.price || !this.price.salePrice) {
			return { dollars: 0, price: 0 };
		}

		const clubPriceNotLoggedInOrClubCardMember =
			this.price.isClubPrice &&
			(!this.shopper.isShopper || !this.shopper.hasOnecard) &&
			!this.price.canShowOriginalPrice;

		/**
		 * For items which are restricted (Cigarettes) or Alchohol
		 * we omit showing the sale price to the user if they
		 * are not logged in
		 */
		let price: string[] | number =
			(clubPriceNotLoggedInOrClubCardMember ? this.price.originalPrice : this.price.salePrice) || 0;

		price = (<number>price).toFixed(2).split('.');

		return {
			dollars: price[0] || '0',
			cents: price[1] || '00',
		};
	}

	get canShowSavings(): boolean {
		return this.price?.canShowSavings !== undefined ? this.price.canShowSavings : true;
	}
	get isCPUUIAdjustimentOn(): boolean {
		return this.featureService.isFeatureEnabled(ContextResponse.EnabledFeaturesEnum.EnableCUPUIAdjustments);
	}
	getPriceAsLabel(isAria = false): string | null {
		if (
			this.supportsEachAndKgPricing &&
			this.selectedPurchasingUnit === ProductResponse.SelectedPurchasingUnitEnum.Each
		) {
			return null;
		}

		const { dollars, cents } = this.presentPrice;
		const perKgLabel = isAria ? 'per kg.' : '<span class="sr-only">per </span>kg';

		return `$${dollars}.${cents} ${this.isKgItem ? perKgLabel : isAria ? 'each.' : 'each'}`;
	}

	getSavePriceLabel(type: PriceType, price?: number): string {
		return price ? `${type} ${price}$` : '';
	}

	openChangedPriceModal(): void {
		if (
			this.price.orderedPrice === undefined ||
			this.price.orderedPrice === null ||
			this.price.salePrice === undefined ||
			this.price.salePrice === null
		) {
			return;
		}

		this.productService.openChangedPriceModal(
			null,
			{
				requestedItemOldSellPrice: this.price.orderedPrice,
				requestedItemNewSellPrice: this.price.salePrice,
			},
			{} as ProductSource,
			false,
			false
		);
	}
}
